import { acceptJsonHeaders, csrfHeaders, handleJson, jsonHeaders } from "@/lib/json-request"
import type { ShotFrameSource } from "@/lib/workspace-api"
import type { JSONContent } from "@tiptap/core"

export type CharacterMediaVersionRow = {
  id: number
  media_kind: string
  view: string | null
  preview_url: string | null
  display_url?: string | null
  download_url?: string | null
  poster_url?: string | null
  origin: string
  created_at: string
  active: boolean
}

export type PortraitViewRow = {
  view: string
  url: string | null
  display_url?: string | null
  download_url?: string | null
  thumb_url?: string | null
  active_version_id: number | null
  origin: string | null
}

export type CharacterVideoMediaKind = "expression_sheet" | "gait_reference" | "action_beat" | "voice_demo"

export type CharacterMediaVersionCounts = Record<CharacterVideoMediaKind, number>

export type CharacterPortraitVersionCounts = Record<string, number>

export type CharacterMediaKind = "portrait" | CharacterVideoMediaKind

export type CharacterReferenceSheetScope = "full_body" | "headless"

export const CHARACTER_REFERENCE_SHEET_SCOPES: CharacterReferenceSheetScope[] = ["full_body", "headless"]

export type CharacterReferenceVersionRow = {
  id: number
  outfit_id: number | null
  sheet_scope?: CharacterReferenceSheetScope | null
  preview_url: string | null
  download_url?: string | null
  origin: string
  created_at: string
  active: boolean
}

export type CharacterAssetSheetKind = "character_sheet" | "character_emotion_sheet"

export type CharacterAssetSheetVersionRow = {
  id: number
  preview_url: string | null
  download_url?: string | null
  origin: string
  created_at: string
  active: boolean
}

export type CharacterSheetVersionRow = CharacterAssetSheetVersionRow

export type CharacterEmotionSheetVersionRow = CharacterAssetSheetVersionRow

export type CharacterAssetSheetVersionOps = {
  list: () => Promise<CharacterAssetSheetVersionRow[]>
  activate: (versionId: number) => Promise<{ ok: boolean; version: CharacterAssetSheetVersionRow }>
  delete: (versionId: number) => Promise<{ ok: boolean }>
}

type ActiveVersionListRow = {
  id: number
  active: boolean
  created_at: string
}

export function characterAssetSheetVersionOps(
  projectId: number,
  characterId: number,
  kind: CharacterAssetSheetKind,
): CharacterAssetSheetVersionOps {
  if (kind === "character_sheet") {
    return {
      list: () => listCharacterSheetVersions(projectId, characterId),
      activate: (versionId) => activateCharacterSheetVersion(projectId, characterId, versionId),
      delete: (versionId) => deleteCharacterSheetVersion(projectId, characterId, versionId),
    }
  }

  return {
    list: () => listCharacterEmotionSheetVersions(projectId, characterId),
    activate: (versionId) => activateCharacterEmotionSheetVersion(projectId, characterId, versionId),
    delete: (versionId) => deleteCharacterEmotionSheetVersion(projectId, characterId, versionId),
  }
}

export function applyActiveVersionListDelete<T extends ActiveVersionListRow>(list: T[], versionId: number): T[] {
  const deleted = list.find((v) => v.id === versionId)
  const next = list.filter((v) => v.id !== versionId)
  if (!deleted?.active || next.length === 0) return next

  const fallbackId = next.reduce((best, v) => (v.created_at > best.created_at ? v : best)).id
  return next.map((v) => ({ ...v, active: v.id === fallbackId }))
}

export function applyAssetSheetVersionDelete(
  list: CharacterAssetSheetVersionRow[],
  versionId: number,
): CharacterAssetSheetVersionRow[] {
  return applyActiveVersionListDelete(list, versionId)
}

export type CharacterPromptPreview = {
  system: string
  user: string
  outfit_id: number | null
}

export type CharacterGenerationSettings = {
  portrait_views: string[]
  reference_views: string[]
  emotions: string[]
  emotion_framing: "head_shoulders" | "waist"
  emotion_angle: "front" | "three_quarter"
  emotion_captions: boolean
  sheet_format: "3:2" | "16:9" | "9:16" | "1:1"
  sheet_parts: string[]
  video_reference_source: "reference_sheet" | "character_sheet"
}

export type CharacterOutfitKind = "on_character" | "garment"

export type CharacterWritePayload = {
  name?: string
  generation_settings?: Partial<CharacterGenerationSettings>
  height?: string | null
  profile_text?: string | null
  profile_doc?: JSONContent | null
  task_id?: number | null
}

export async function createCharacter(projectId: number, payload: CharacterWritePayload) {
  const res = await fetch(`/projects/${projectId}/workspace/characters`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ character: payload }),
  })
  return handleJson<{ ok: boolean; character_id: number; message?: string }>(res)
}

export async function updateCharacter(projectId: number, characterId: number, payload: Partial<CharacterWritePayload>) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ character: payload }),
  })
  return handleJson<{ ok: boolean; message?: string; sheet_version_reused?: boolean }>(res)
}

export async function deleteCharacter(projectId: number, characterId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function previewReferencePrompt(
  projectId: number,
  characterId: number,
  outfitId: number | null,
): Promise<CharacterPromptPreview> {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/preview_reference_prompt`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ outfit_id: outfitId }),
  })
  return handleJson<CharacterPromptPreview>(res)
}

export async function generateCharacterReference(
  projectId: number,
  characterId: number,
  outfitId: number | null,
  opts?: { correctionPrompt?: string; sourceVersionId?: number; sheetScope?: CharacterReferenceSheetScope },
) {
  const body: Record<string, unknown> = { outfit_id: outfitId }
  if (opts?.correctionPrompt) body.correction_prompt = opts.correctionPrompt
  if (opts?.sourceVersionId) body.source_version_id = opts.sourceVersionId
  if (opts?.sheetScope) body.sheet_scope = opts.sheetScope
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/generate_reference`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function generatePortrait(
  projectId: number,
  characterId: number,
  opts?: { correctionPrompt?: string; sourceMediaId?: number; view?: string },
) {
  const body: Record<string, unknown> = {}
  if (opts?.correctionPrompt) body.correction_prompt = opts.correctionPrompt
  if (opts?.sourceMediaId) body.source_media_id = opts.sourceMediaId
  if (opts?.view) body.view = opts.view
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/generate_portrait`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function generateCharacterMedia(projectId: number, characterId: number, mediaKind: CharacterMediaKind) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/generate_media`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ media_kind: mediaKind }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export type SeedanceCharacterVideoConsentOwner = { type: string; id: number; label: string }

export type CheckSeedanceCharacterVideoConsentResponse = {
  ok: boolean
  needs_consent: boolean
  character_id: number
  media_kind: CharacterVideoMediaKind
  owners: SeedanceCharacterVideoConsentOwner[]
  agreement_key: string
}

export async function checkSeedanceCharacterVideoConsent(
  projectId: number,
  characterId: number,
  mediaKind: CharacterVideoMediaKind,
): Promise<CheckSeedanceCharacterVideoConsentResponse> {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/check_seedance_character_video_consent`,
    {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({ media_kind: mediaKind }),
    },
  )
  return handleJson<CheckSeedanceCharacterVideoConsentResponse>(res)
}

export async function registerSeedanceCharacterVideoConsent(
  projectId: number,
  characterId: number,
  mediaKind: CharacterVideoMediaKind,
  agreementVersion: number,
) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/register_seedance_character_video_consent`,
    {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({ media_kind: mediaKind, agreement_version: agreementVersion }),
    },
  )
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function correctCharacterMedia(
  projectId: number,
  characterId: number,
  mediaKind: CharacterMediaKind,
  correctionPrompt: string,
  sourceMediaId?: number | null,
) {
  const body: Record<string, unknown> = { media_kind: mediaKind, correction_prompt: correctionPrompt }
  if (sourceMediaId) body.source_media_id = sourceMediaId
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/correct_media`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function listMediaVersions(
  projectId: number,
  characterId: number,
  mediaKind: CharacterMediaKind,
  view?: string | null,
): Promise<CharacterMediaVersionRow[]> {
  const params = new URLSearchParams({ media_kind: mediaKind })
  if (view) params.set("view", view)
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/media_versions?${params.toString()}`,
    {
      headers: acceptJsonHeaders(),
      credentials: "same-origin",
    },
  )
  const data = await handleJson<{ versions: CharacterMediaVersionRow[] }>(res)
  return data.versions
}

export async function uploadMediaVersion(
  projectId: number,
  characterId: number,
  file: File,
  mediaKind: CharacterMediaKind,
  view?: string | null,
) {
  const form = new FormData()
  form.append("file", file)
  form.append("media_kind", mediaKind)
  if (view) form.append("view", view)

  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/media_versions`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; version: CharacterMediaVersionRow }>(res)
}

export async function createMediaVersionFromSource(
  projectId: number,
  characterId: number,
  source: ShotFrameSource,
  mediaKind: CharacterMediaKind,
  view?: string | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/media_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source, media_kind: mediaKind, view: view ?? undefined }),
  })
  return handleJson<{ ok: boolean; version: CharacterMediaVersionRow }>(res)
}

export async function clearCharacterMediaVersion(
  projectId: number,
  characterId: number,
  opts: { mediaKind: CharacterMediaKind; view?: string | null },
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/media_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      clear: true,
      media_kind: opts.mediaKind,
      view: opts.view ?? undefined,
    }),
  })
  return handleJson<{ ok: boolean; version: CharacterMediaVersionRow }>(res)
}

export async function activateMediaVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/media_versions/${versionId}/activate`,
    {
      method: "PATCH",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({}),
    },
  )
  return handleJson<{ ok: boolean; version: CharacterMediaVersionRow }>(res)
}

export async function deleteMediaVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/media_versions/${versionId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean }>(res)
}

export async function addReferenceInput(
  projectId: number,
  characterId: number,
  file: File,
  outfitId?: number | null,
  meta?: { sourceReferenceKey?: string; mediaKind?: CharacterVideoMediaKind },
) {
  const form = new FormData()
  form.append("file", file)
  if (outfitId) form.append("outfit_id", String(outfitId))
  if (meta?.sourceReferenceKey) form.append("source_reference_key", meta.sourceReferenceKey)
  if (meta?.mediaKind) form.append("media_kind", meta.mediaKind)
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/add_reference_input`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

/** Референс из Библиотеки: сервер берёт существующий блоб, файл не копируется. */
export async function addReferenceInputFromSource(
  projectId: number,
  characterId: number,
  source: ShotFrameSource,
  outfitId?: number | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/add_reference_input`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source, outfit_id: outfitId ?? undefined }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function removeReferenceInput(
  projectId: number,
  characterId: number,
  blobSignedId: string,
  outfitId?: number | null,
  meta?: { mediaKind?: CharacterVideoMediaKind },
) {
  const body: Record<string, unknown> = { blob_signed_id: blobSignedId }
  if (outfitId) body.outfit_id = outfitId
  if (meta?.mediaKind) body.media_kind = meta.mediaKind
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/remove_reference_input`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export type AgreementPayload = {
  key: string
  title: string
  body: string
  version: number
}

export async function fetchAgreement(key: string): Promise<AgreementPayload> {
  const res = await fetch(`/agreements/${encodeURIComponent(key)}`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<AgreementPayload>(res)
}

function seedanceAgreementBody(agreementVersion?: number): Record<string, unknown> {
  return agreementVersion ? { agreement_version: agreementVersion } : {}
}

export async function registerSeedanceAvatar(projectId: number, characterId: number, agreementVersion?: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/register_seedance_avatar`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(seedanceAgreementBody(agreementVersion)),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function registerProjectSeedanceAvatars(projectId: number, agreementVersion?: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/register_seedance_avatars`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(seedanceAgreementBody(agreementVersion)),
  })
  return handleJson<{ ok: boolean; message?: string; queued_count: number; skipped_count: number }>(res)
}

export async function registerOutfitSeedanceAvatar(
  projectId: number,
  characterId: number,
  outfitId: number,
  agreementVersion?: number,
) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/outfits/${outfitId}/register_seedance_avatar`,
    {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(seedanceAgreementBody(agreementVersion)),
    },
  )
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function createOutfit(
  projectId: number,
  characterId: number,
  payload: { name: string; description?: string | null; kind?: CharacterOutfitKind },
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/outfits`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ outfit: payload }),
  })
  return handleJson<{ ok: boolean; outfit_id: number; message?: string }>(res)
}

export async function updateOutfit(
  projectId: number,
  characterId: number,
  outfitId: number,
  payload: { name?: string; description?: string | null; kind?: CharacterOutfitKind },
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/outfits/${outfitId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ outfit: payload }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function deleteOutfit(projectId: number, characterId: number, outfitId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/outfits/${outfitId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function listReferenceVersions(
  projectId: number,
  characterId: number,
  outfitId: number | null | undefined,
  sheetScope?: CharacterReferenceSheetScope,
): Promise<CharacterReferenceVersionRow[]> {
  const params = new URLSearchParams()
  if (outfitId === null) params.set("outfit_id", "")
  else if (typeof outfitId === "number") params.set("outfit_id", String(outfitId))
  if (sheetScope) params.set("sheet_scope", sheetScope)
  const qs = params.toString() ? `?${params.toString()}` : ""
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/reference_versions${qs}`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  const data = await handleJson<{ versions: CharacterReferenceVersionRow[] }>(res)
  return data.versions
}

export async function uploadReferenceVersion(
  projectId: number,
  characterId: number,
  file: File,
  outfitId: number | null,
  sheetScope?: CharacterReferenceSheetScope,
) {
  const form = new FormData()
  form.append("file", file)
  if (outfitId !== null) form.append("outfit_id", String(outfitId))
  if (sheetScope) form.append("sheet_scope", sheetScope)

  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/reference_versions`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; version: CharacterReferenceVersionRow }>(res)
}

export async function createReferenceVersionFromSource(
  projectId: number,
  characterId: number,
  source: ShotFrameSource,
  outfitId: number | null,
  sheetScope?: CharacterReferenceSheetScope,
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/reference_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      source,
      outfit_id: outfitId ?? undefined,
      sheet_scope: sheetScope,
    }),
  })
  return handleJson<{ ok: boolean; version: CharacterReferenceVersionRow }>(res)
}

export async function clearCharacterReferenceVersion(
  projectId: number,
  characterId: number,
  opts?: { outfitId?: number | null; sheetScope?: CharacterReferenceSheetScope },
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/reference_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      clear: true,
      outfit_id: opts?.outfitId ?? undefined,
      sheet_scope: opts?.sheetScope,
    }),
  })
  return handleJson<{ ok: boolean; version: CharacterReferenceVersionRow }>(res)
}

export async function activateReferenceVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/reference_versions/${versionId}/activate`,
    {
      method: "PATCH",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({}),
    },
  )
  return handleJson<{ ok: boolean; version: CharacterReferenceVersionRow }>(res)
}

export async function deleteReferenceVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/reference_versions/${versionId}`,
    {
      method: "DELETE",
      headers: jsonHeaders(),
      credentials: "same-origin",
    },
  )
  return handleJson<{ ok: boolean }>(res)
}

export async function generateCharacterSheet(projectId: number, characterId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_sheets`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({}),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function createCharacterSheetFromSource(projectId: number, characterId: number, source: ShotFrameSource) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_sheets`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source }),
  })
  return handleJson<{ ok: boolean; version: CharacterAssetSheetVersionRow }>(res)
}

export async function clearCharacterSheetVersion(projectId: number, characterId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_sheets`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ clear: true }),
  })
  return handleJson<{ ok: boolean; version: CharacterAssetSheetVersionRow }>(res)
}

export async function listCharacterSheetVersions(
  projectId: number,
  characterId: number,
): Promise<CharacterSheetVersionRow[]> {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_sheets`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  const data = await handleJson<{ versions: CharacterSheetVersionRow[] }>(res)
  return data.versions
}

export async function activateCharacterSheetVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/character_sheets/${versionId}/activate`,
    {
      method: "PATCH",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({}),
    },
  )
  return handleJson<{ ok: boolean; version: CharacterSheetVersionRow }>(res)
}

export async function deleteCharacterSheetVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_sheets/${versionId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean }>(res)
}

export async function generateCharacterEmotionSheet(
  projectId: number,
  characterId: number,
  opts?: { correctionPrompt?: string; sourceVersionId?: number },
) {
  const body: Record<string, unknown> = {}
  if (opts?.correctionPrompt) body.correction_prompt = opts.correctionPrompt
  if (opts?.sourceVersionId) body.source_version_id = opts.sourceVersionId
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_emotion_sheets`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function uploadCharacterEmotionSheet(projectId: number, characterId: number, file: File) {
  const form = new FormData()
  form.append("file", file)
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_emotion_sheets`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; version: CharacterAssetSheetVersionRow }>(res)
}

export async function createCharacterEmotionSheetFromSource(
  projectId: number,
  characterId: number,
  source: ShotFrameSource,
) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_emotion_sheets`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source }),
  })
  return handleJson<{ ok: boolean; version: CharacterAssetSheetVersionRow }>(res)
}

export async function clearCharacterEmotionSheetVersion(projectId: number, characterId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_emotion_sheets`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ clear: true }),
  })
  return handleJson<{ ok: boolean; version: CharacterAssetSheetVersionRow }>(res)
}

export async function generateCharacterScalePose(projectId: number, characterId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_scale_poses`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({}),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function listCharacterEmotionSheetVersions(
  projectId: number,
  characterId: number,
): Promise<CharacterEmotionSheetVersionRow[]> {
  const res = await fetch(`/projects/${projectId}/workspace/characters/${characterId}/character_emotion_sheets`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  const data = await handleJson<{ versions: CharacterEmotionSheetVersionRow[] }>(res)
  return data.versions
}

export async function activateCharacterEmotionSheetVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/character_emotion_sheets/${versionId}/activate`,
    {
      method: "PATCH",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({}),
    },
  )
  return handleJson<{ ok: boolean; version: CharacterEmotionSheetVersionRow }>(res)
}

export async function deleteCharacterEmotionSheetVersion(projectId: number, characterId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/characters/${characterId}/character_emotion_sheets/${versionId}`,
    {
      method: "DELETE",
      headers: jsonHeaders(),
      credentials: "same-origin",
    },
  )
  return handleJson<{ ok: boolean }>(res)
}
